import "./App.scss";
import {useEffect, useState, useMemo} from "react";
import {paymentMethodsMap} from "../constants/paymentMethods";
import {validateAccount} from "../util/validate-account";
import {getGeneratePaymentLinkApiUrl} from "../util/get-api-url";
import {
    AccountValidationState,
} from "../constants/accountValidationStates";
import {PERSIST_USER_ID_KEY} from "../constants/commonConstants";
import {Button} from "../components/Button";
import {useSearchParams} from "react-router-dom";
import {useValidationState} from "../util/use-validation-state";
import {Section, SelectableBox, Subtitle, Title} from "../components/ui-kit";
import UserIdInput from "../components/UserIdInput";
import {useI18n} from "../i18n/use-i18n";
import {getFilteredPremiums} from "../util/get-filtered-premiums";
import Head from "../components/Head";
import { getBotName } from "../util/get-bot-name";
import { getBotUrl } from "../util/get-bot-url";
import {chatbotPremiums} from "../constants/premiums/chatbot-premiums";

function Chatbot() {
    const [userId, setUserId] = useState('');
    const [searchParams] = useSearchParams();
    const {translate, language} = useI18n();
    const paymentMethods = paymentMethodsMap[language];
    const [method, setMethod] = useState(paymentMethods[0].identifier);
    const [emptyUserIdError, setEmptyUserIdError] = useState(false);
    const [validationState, setAccountValidationState] = useValidationState(userId);
    const [isFetching, setIsFetching] = useState(false);
    const [activePremium, setActivePremium] = useState('');
    const [premiumFromSearch, setPremiumFromSearch] = useState(false);
    const paramPremium = searchParams.get('premium');
    const paramDiscount = searchParams.get('discount');
    const filteredPremiums = useMemo(() => {
        return getFilteredPremiums(chatbotPremiums[language], paramDiscount);
    }, [paramDiscount, language]);

    const validateAccountAndUpdateState = (userId) => {
        validateAccount(userId)
            .then((res) => {
                if (res) {
                    setAccountValidationState(AccountValidationState.CORRECT_ID);
                } else {
                    setAccountValidationState(AccountValidationState.WRONG_ID);
                }
            })
            .catch(() => setAccountValidationState(AccountValidationState.WRONG_ID));
    }

    useEffect(() => {
        // When language changes set new active premium if it was not set already
        // When user has "id" language then at first the page mounts with "en" and only then changes to "id"
        if (!premiumFromSearch && paramPremium && filteredPremiums.find((p) => p.identifier === paramPremium)) {
            // If paramPremium exists, it's valid and wasn't used earlier then set it
            setActivePremium(paramPremium);
            setPremiumFromSearch(true);
        } else if (!activePremium || (activePremium && filteredPremiums.findIndex((p) => p.identifier === activePremium) === -1)) {
            // If no active premium is set or the active one isn't valid then set the default one
            setActivePremium(language === 'en' ? 'month' : 'month_id');
        }
    }, [language, activePremium, filteredPremiums, paramPremium, premiumFromSearch]);

    useEffect(() => {
        const searchParamsUserId = searchParams.get('userId');
        const localStorageUserId = localStorage.getItem(PERSIST_USER_ID_KEY);
        const persistedUserId = searchParamsUserId || localStorageUserId;
        if (persistedUserId) {
            // Restore persisted uid on mount
            setUserId(persistedUserId);
            validateAccountAndUpdateState(persistedUserId);
        }
        const paramPremium = searchParams.get('premium');
        if (paramPremium && filteredPremiums.find((p) => p.identifier === paramPremium)) {
            setActivePremium(paramPremium);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    async function onFormSubmit(event) {
        event.preventDefault();

        if (AccountValidationState.CORRECT_ID !== validationState) {
            return;
        }

        if (userId.length === 0) {
            setEmptyUserIdError(true);
            return;
        }
        // If all checks were passed then persist uid
        localStorage.setItem(PERSIST_USER_ID_KEY, userId);
        setIsFetching(true);
        const premium = filteredPremiums.find((p) => p.identifier === activePremium);
        const urlParams = new URLSearchParams({
            userId,
            method,
            user_lang: language,
            premium: premium.identifier,
        });
        fetch(getGeneratePaymentLinkApiUrl() + `?${urlParams.toString()}`)
            .then(async (response) => {
                if (response.status === 200) {
                    const {url} = await response.json();
                    window.location.href = url;
                } else {
                    alert(`Something went wrong while creating payment link: ${response.statusText} (${await response.text()})`);
                }
            })
            .catch(() => {
                alert('Something went wrong');
            })
            .finally(() => setIsFetching(false));
    }

    return (
        <div className={'App-container'}>
            <Head/>
            <form onSubmit={onFormSubmit}>
                <h1 className={'App-header'}>
                    {translate("premium-in")} {getBotName()}
                </h1>
                <Section>
                    <Title>
                        {translate("enter-user-id")}
                    </Title>
                    <Subtitle>
                        {translate("get-user-id-hint")} <a href={getBotUrl()} target="_blank" rel="noreferrer" className="App-link-blue">{getBotName()}</a>
                    </Subtitle>
                    <UserIdInput
                        validationState={validationState}
                        setAccountValidationState={setAccountValidationState}
                        setUserId={setUserId}
                        emptyUserIdError={emptyUserIdError}
                        validateAccountAndUpdateState={validateAccountAndUpdateState}
                        userId={userId}
                    />
                    {emptyUserIdError && AccountValidationState.CORRECT_ID !== validationState &&
                        <label className={'App-input-invalid-label'}>{translate("enter-user-id")}</label>}
                </Section>
                <Section>
                    <Title>
                        {translate("choose-plan")}
                    </Title>
                    {filteredPremiums.map((item) => {
                        const {identifier, fancyName} = item;
                        const isChecked = identifier === activePremium;
                        return <SelectableBox
                            key={identifier}
                            identifier={identifier}
                            onClick={setActivePremium}
                            isChecked={isChecked}
                            title={fancyName}
                            subtitle={item.getFancyPrice()}
                        />;
                    })}
                </Section>
                <Section>
                    <Title>
                        {translate("choose-payment-method")}
                    </Title>
                    {paymentMethods.map(({identifier, title, extra}) => {
                        const isChecked = identifier === method;
                        return <SelectableBox
                            key={identifier}
                            identifier={identifier}
                            onClick={setMethod}
                            isChecked={isChecked}
                            title={translate(title)}
                            subtitle={translate(extra)}
                        />;
                    })}
                    <Button
                        onClick={onFormSubmit}
                        title={translate("go-to-payment")}
                        isDisabled={AccountValidationState.CORRECT_ID !== validationState}
                        isFetching={isFetching}
                    />
                    {validationState === AccountValidationState.WRONG_ID && <span className={'App-wrong-user-id-error'}>
                        {translate("user-id-does-not-exist")}
            </span>}
                </Section>
            </form>
        </div>
    );
}

export default Chatbot;
